/**
 * Tiny status placeholders. Every API-bound component renders one of these
 * three states so we never silently leave the user staring at a blank panel.
 */

import { ApiError } from "@/lib/api";

export function LoadingBlock({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-6 text-sm text-brand-dim">
      <span className="inline-block h-2 w-2 rounded-full bg-brand-orange animate-pulse" />
      {label}
    </div>
  );
}

export function EmptyBlock({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="px-4 py-10 text-center text-sm text-brand-dim">
      <div className="text-slate-200 font-medium">{title}</div>
      {description ? <div className="mt-1">{description}</div> : null}
    </div>
  );
}

export function ErrorBlock({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  let title = "Something went wrong.";
  let detail: string | undefined;
  if (error instanceof ApiError) {
    title = `${error.status || "—"} ${error.reason}`;
    detail = error.message;
  } else if (error instanceof Error) {
    detail = error.message;
  }
  return (
    <div className="mx-4 my-4 rounded-md border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
      <div className="font-semibold">{title}</div>
      {detail ? (
        <div className="mt-1 text-rose-200/80 break-all">{detail}</div>
      ) : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 inline-flex items-center rounded border border-rose-300/40 px-2 py-1 text-xs uppercase tracking-wider text-rose-100 hover:bg-rose-500/20"
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}
